<template>
    <div>
        <AutomationEmailEditorContainer
            :is-ready="isReady"
            :title="title"
            :updated-time="updatedTime"
            @close="handleClose"
        >
            <template #header>
                <DsTextButton
                    data-qa="send-test-button"
                    :disabled="isInitializing"
                    :loading="isSendTestButtonInLoadingState"
                    @click="handleSendTestClick"
                >
                    {{ $t('unlayerBuilder.templateSelection.menu.sendATest') }}
                </DsTextButton>

                <DsSpinner
                    v-if="isReadyStateLoading"
                    class="ready-status-spinner"
                    :size="25"
                />

                <DsToggleSwitch
                    v-else
                    :readonly="isInitializing"
                    :value="isReady"
                    :label-on="$t('funnel.header.readyToggle.ready')"
                    :label-off="$t('funnel.header.readyToggle.draft')"
                    class="sequence-toggle"
                    @input="handleReady"
                />

                <DsButton
                    :disabled="isInitializing"
                    :loading="isDoneButtonInLoadingState"
                    data-qa="automation-email-done-button"
                    class="automation-email-done"
                    @click="handleDone"
                >
                    <span>{{ $t('funnel.edit.done') }}</span>
                </DsButton>
            </template>

            <template #default>
                <div class="editor-content">
                    <div v-if="isInitializing" class="init-spinner-container">
                        <DsSpinner />
                    </div>

                    <p v-else-if="loadError" style="text-align: center;">
                        {{ $t('funnel.unlayerEmail.error.loadingEmail') }}
                    </p>

                    <template v-else>
                        <div v-if="showErrors && errors.length > 0" class="error-container">
                            <DsInlineAlert
                                v-for="error of errors"
                                :key="error.message"
                                leading-icon
                                type="critical"
                            >
                                {{ error.message }}
                            </DsInlineAlert>
                        </div>

                        <StepsContainer>
                            <component
                                :is="step.component"
                                v-for="step in steps"
                                :key="step.title"
                                :data-qa="step.title"
                                :active="isStepActive(step)"
                                :email="email"
                                :environment="environment"
                                :funnel-id="funnelId"
                                :merge-fields="mergeFields"
                                :show-errors="isStepError(step)"
                                :has-content="hasContent"
                                :html="email[$options.STEPS.CONTENT].htmlBody"
                                :saved-to-address="email[$options.STEPS.AUDIENCE].toAddress"
                                :saved-from="email[$options.STEPS.SENDER].from"
                                :saved-reply-to-address="email[$options.STEPS.SENDER].replyToAddress"
                                :saved-subject="email[$options.STEPS.SUBJECT].subject"
                                :saved-preview-text="email[$options.STEPS.SUBJECT].previewText"
                                :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                                :liquid-merge-fields-in-automations-enabled="liquidMergeFieldsInAutomationsEnabled"
                                :content-risk-messages="contentRiskMessages"
                                :content-risk-quality="contentRiskQuality"
                                :content-risk-sendable="contentRiskSendable"
                                :fetch-content-risks="fetchContentRisks"
                                :fetch-subject-risks="fetchSubjectRisks"
                                :loading-subject-risk="loadingSubjectRisk"
                                :loading-content-risk="loadingContentRisk"
                                :subject-risk-messages="subjectRiskMessages"
                                :subject-risk-quality="subjectRiskQuality"
                                :subject-risk-sendable="subjectRiskSendable"
                                :preview-risk-sendable="previewRiskSendable"
                                :preview-risk-quality="previewRiskQuality"
                                :preview-risk-messages="previewRiskMessages"
                                :loading-preview-risk="loadingPreviewRisk"
                                :fetch-preview-risks="fetchPreviewRisks"
                                :is-max-classic="isMaxClassic"
                                preview-spam-check-enabled
                                @load-preview="loadPreview"
                                @cancel="cancelEditStep"
                                @error="handleStepError(step, $event)"
                                @save="saveStep(step, $event)"
                            >
                                <template #title>
                                    <StepTitle
                                        :active="isStepActive(step)"
                                        :complete="step.status === $options.STATUSES.COMPLETE"
                                        :error="isStepError(step)"
                                        :title="$t(`email.broadcast.editor.sectionTitle.${step.title.toLowerCase()}`)"
                                    />
                                </template>

                                <template #action="{ actionText }">
                                    <component
                                        :is="isStepNew(step) ? 'DsOutlineButton' : 'DsTextButton'"
                                        v-if="!isStepActive(step)"
                                        class="step-action"
                                        dense
                                        :disabled="!!activeStepTitle"
                                        @click="activateStep(step)"
                                    >
                                        {{ getActionText(step, actionText) }}
                                    </component>
                                </template>
                            </component>
                        </StepsContainer>
                    </template>
                </div>

                <UnlayerWizardModal
                    v-if="showUnlayerWizard"
                    :is-open="isUnlayerWizardModalOpen"
                    :initial-stage="wizardStageIndex"
                    :email="email"
                    :funnel-id="funnelId"
                    :is-max-classic="isMaxClassic"
                    :marketing-email-id="marketingEmailId"
                    :external-template-id="externalTemplateId"
                    :core-config="coreConfig"
                    :content-risk-messages="contentRiskMessages"
                    :content-risk-quality="contentRiskQuality"
                    :content-risk-sendable="contentRiskSendable"
                    :loading-content-risk="loadingContentRisk"
                    :fetch-content-risks="fetchContentRisks"
                    :tracked-links="trackedLinks"
                    @template-created="handleTemplateChange"
                    @update-template="handleTemplateUpdate"
                    @template-selected="handleTemplateChange"
                    @unsaved-changes="handleUnsavedChanges"
                    @close="handleUnlayerWizardModalClose"
                />
            </template>
        </AutomationEmailEditorContainer>

        <ContentRiskActions v-slot="props">
            <SendTestEmailModal
                v-if="isModalOpen"
                :is-open="isModalOpen"
                :from-address="email[$options.STEPS.SENDER].from"
                :html-body="email[$options.STEPS.CONTENT].htmlBody"
                :subject="email[$options.STEPS.SUBJECT].subject"
                :preview-text="email[$options.STEPS.SUBJECT].previewText"
                :funnel-id="funnelId"
                :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                v-bind="props"
                @close="isModalOpen = false"
            />
        </ContentRiskActions>
    </div>
</template>

<script>
import {
    STATUS_TYPE,
    DEFAULT_FROM_VALUE,
    DEFAULT_RECIPIENT_VALUES_LIQUID_SYNTAX,
} from '@/components/broadcast/constants/emailBroadcast.constants.js';
import {
    STATUSES,
    STEPS,
    STATUS_ICONS,
    STEP_ERRORS,
} from '@/components/unlayer/constants/editor.constants';
import {
    SOURCES,
    UNLAYER_EMAIL_BROADCAST_WIZARD_STAGE_INDEXES as STAGE_INDEXES,
} from '@/components/unlayer/constants/unlayer.constants';
import {
    createNewUnlayerEmail,
    validateUnlayerEmail,
    replaceCampaignLinkIdsWithKeys,
    replaceCampaignMergeFieldIdsWithKeys,
    containsAnyCampaignMergeFieldWithId,
} from '@/components/unlayer/unlayerEmail.util';
import {
    getFunnelEmail,
    saveFunnelEmail,
    getCampaignLinks,
    getFunnelMergeFields,
    getMergeFields,
    getFunnelPreviewHtml,
} from '@/api/campaigns/campaignUnlayerEmail';
import { CONTENT_RISK_QUALITY, CONTENT_RISK_STATUS } from './content-risk.constants';

import AutomationEmailEditorContainer from './AutomationEmailEditorContainer.vue';

import StepsContainer from './UnlayerEmailStepsContainer.vue';
import StepTitle from './UnlayerEmailStepTitle.vue';

import SenderStep from '@/components/broadcast/email/steps/EmailBroadcastSenderStep';
import SubjectStep from '@/components/broadcast/email/steps/EmailBroadcastSubjectStep';
import AudienceStep from '@/components/broadcast/email/steps/EmailAutomationAudienceStep';
import ContentStep from '@/components/broadcast/email/steps/EmailBroadcastContentStep';
import UnlayerWizardModal from '@/components/broadcast/email/UnlayerEmailBroadcastWizardModal';
import SendTestEmailModal from './sendTestEmail/SendTestEmailModal.vue';
import ContentRiskActions from './ContentRiskActions.vue';

const ERROR_CATEGORIES = {
    READY_TOGGLE: 'READY_TOGGLE',
};

// Matches ExpiredJwtSubjectException.REASON_CODE / UnlayerEmailFunnelController's save-path 403 (see
// CUS-13116). Checked explicitly so this UX only fires for an expired session, not any other 403.
const EXPIRED_JWT_SUBJECT_REASON_CODE = 'EXPIRED_JWT_SUBJECT';

// Same URL session.js global XHR handler navigates to on a 401 (see initializeXhr() in
// webapp/src/main/webapp/resources/session/session.js) -- forces a fresh login. Opened in a new tab
// here (rather than navigating this one, like session.js does), so the user's in-progress, unsaved
// edits in this editor are never lost.
const SESSION_EXPIRED_LOGIN_URL = '/j_spring_security_logout?timeout=true';

export default {
    STATUS_TYPE,
    STATUSES,
    STEPS,

    name: 'AutomationEmailEditorPage',

    components: {
        AutomationEmailEditorContainer,
        StepsContainer,
        StepTitle,
        UnlayerWizardModal,
        SendTestEmailModal,
        ContentRiskActions,
    },

    props: {
        marketingEmailId: {
            type: String,
        },
        funnelId: {
            type: String,
            default: '',
        },
        title: {
            type: String,
        },
        isKonva: Boolean,
        isCellReady: Boolean,
        updatedTime: String,
        unlayerBuilderVersionOverride: String,
        appId: String,
        userId: String,
        environment: String,
        unlayerProjectId: String,
        bookingLinkHostname: String,

        // flags
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
        unlayerAiToolsEnabled: Boolean,
        unlayerSmartMergeTagsEnabled: Boolean,
        useUnlayerBuilderVersionOverrideEnabled: Boolean,
        unlayerEmailLogoToolEnabled: Boolean,
        autoOpenContent: Boolean,
        isMaxClassic: Boolean,
        liquidMergeFieldsInAutomationsEnabled: Boolean,
        emailsThryvThemeEnabled: Boolean,
        unlayerExtraTagsOverrideEnabled: Boolean,
        thryvInterimUnifiedThemeEnabled: Boolean,

        // content risk
        contentRiskMessages: {
            type: Array,
            default: () => [],
        },
        contentRiskQuality: String,
        contentRiskSendable: Boolean,
        loadingContentRisk: Boolean,
        contentRiskStatus: String,
        fetchContentRisks: {
            type: Function,
            default: (x) => x,
        },

        // subject risk
        fetchSubjectRisks: {
            type: Function,
            default: (x) => x,
        },
        loadingSubjectRisk: Boolean,
        subjectRiskMessages: {
            type: Array,
            default: () => [],
        },
        subjectRiskQuality: String,
        subjectRiskSendable: Boolean,
        subjectRiskStatus: String,

        // preview risk
        previewRiskSendable: Boolean,
        previewRiskQuality: String,
        previewRiskMessages: {
            type: Array,
            default: () => [],
        },
        loadingPreviewRisk: Boolean,
        fetchPreviewRisks: {
            type: Function,
            default: (x) => x,
        },
    },

    data() {
        return {
            isReady: false,
            isInitializing: true,
            loadError: false,
            showErrors: false,
            isUnlayerWizardModalOpen: false,
            activeStepTitle: null,
            email: createNewUnlayerEmail(SOURCES.CAMPAIGN),
            externalTemplateId: null,
            steps: [{
                component: SenderStep,
                status: STATUSES.COMPLETE,
                title: STEPS.SENDER,
            }, {
                component: SubjectStep,
                status: STATUSES.NEW,
                title: STEPS.SUBJECT,
            }, {
                component: AudienceStep,
                status: STATUSES.COMPLETE,
                title: STEPS.AUDIENCE,
            }, {
                component: ContentStep,
                status: STATUSES.NEW,
                title: STEPS.CONTENT,
            }],
            errors: [],
            trackedLinks: [],
            campaignLinks: [],
            funnelMergeFields: [],
            mergeFields: [],
            isModalOpen: false,
            isReadyStateLoading: false,
            isSendTestButtonInLoadingState: false,
            isDoneButtonInLoadingState: false,
            isSubjectRiskUptoDate: false,
            isContentRiskUptoDate: false,
            wizardStageIndex: STAGE_INDEXES.TEMPLATE,
        };
    },

    async created() {
        this.isReady = this.isCellReady;

        if (!this.isKonva) {
            // remove when polymer is deprecated
            window.addEventListener('campaignUnlayerEmailBuilderModalOpened', (event) => {
                this.isReady = Boolean(event.detail.isReady);
            });
            window.parent.dispatchEvent(new Event('campaignUnlayerEmailBuilderModalCreated'));
        }

        try {
            const template = await getFunnelEmail({
                    marketingEmailId: this.marketingEmailId,
                    funnelId: this.funnelId,
                });

            if (template) {
                this.externalTemplateId = template.externalTemplateId;
            }

            await this.loadEmail();

            if (this.autoOpenContent && this.hasContent) {
                this.activateStep({ title: STEPS.CONTENT });
            }
        } catch (e) {
            this.loadError = true;
        }

        this.isInitializing = false;
    },

    beforeDestroy() {
        if (!this.isKonva) {
            window.removeEventListener('campaignUnlayerEmailBuilderModalOpened', this.handleModalOpened);
        }
    },

    watch: {
        async errors() {
            if (this.stepErrors.length > 0) return;

            setTimeout(() => {
                if (this.stepErrors.length > 0) return; // might have changed by this time

                this.removeAllErrorsForCategory(ERROR_CATEGORIES.READY_TOGGLE);
            }, 0);
        },
    },

    computed: {
        coreConfig() {
            return {
                appId: this.appId,
                userId: this.userId,
                environment: this.environment,
                unlayerProjectId: this.unlayerProjectId,
                bookingLinkHostname: this.bookingLinkHostname,

                // flags
                unlayerAiToolsEnabled: this.unlayerAiToolsEnabled,
                unlayerSmartMergeTagsEnabled: this.unlayerSmartMergeTagsEnabled,
                unlayerBuilderVersionOverride: this.unlayerBuilderVersionOverride,
                showVerifiedDomainsNewKeysOnlyEnabled: this.showVerifiedDomainsNewKeysOnlyEnabled,
                useUnlayerBuilderVersionOverrideEnabled: this.useUnlayerBuilderVersionOverrideEnabled,
                unlayerEmailLogoToolEnabled: this.unlayerEmailLogoToolEnabled,
                emailsThryvThemeEnabled: this.emailsThryvThemeEnabled,
                isUnlayerExtraTagOverrideEnabled: this.unlayerExtraTagsOverrideEnabled,
                thryvInterimUnifiedThemeEnabled: this.thryvInterimUnifiedThemeEnabled,
            };
        },

        emailTemplate() {
            const {
                [STEPS.SUBJECT]: { subject, previewText },
                [STEPS.CONTENT]: { htmlBody },
                [STEPS.SENDER]: { from, replyToAddress },
            } = this.email;

            return {
                from,
                replyToAddress,
                htmlBody,
                subject,
                previewText,
            };
        },

        stepErrors() {
            const steps = Object.values(STEPS);

            return this.errors.filter(({ category }) => steps.includes(category));
        },

        hasContent() {
            return validateUnlayerEmail(this.email, STEPS.CONTENT);
        },

        showUnlayerWizard() {
            return !this.isInitializing && !!this.email;
        },
    },

    methods: {
        async validateEmail(steps) {
            this.showErrors = true;

            const defaultValidator = ({
                errorMessageTranslationString,
                step,
            }) => {
                if (step.status === STATUSES.ERROR) {
                    return false;
                }

                if (step.status !== STATUSES.COMPLETE) {
                    this.errorAdd({
                        message: this.$t(errorMessageTranslationString),
                        category: step.title,
                    });

                    return false;
                }

                return true;
            };

            let senderStep;
            let subjectStep;
            let audienceStep;
            let contentStep;

            this.steps.forEach((step) => {
                switch (step.title) {
                case STEPS.SENDER:
                    senderStep = step;
                    break;
                case STEPS.SUBJECT:
                    subjectStep = step;
                    break;
                case STEPS.AUDIENCE:
                    audienceStep = step;
                    break;
                case STEPS.CONTENT:
                    contentStep = step;
                    break;
                default:
                    throw new Error('Unknown step');
                }
            });

            const validators = {
                [STEPS.SENDER]: () => defaultValidator({
                    errorMessageTranslationString: 'email.broadcast.editor.errors.step.sender',
                    step: senderStep,
                }),

                [STEPS.SUBJECT]: async () => {
                    const isValid = defaultValidator({
                        errorMessageTranslationString: 'email.broadcast.editor.errors.step.subject',
                        step: subjectStep,
                    });

                    if (!isValid) return false;

                    if (!this.isSubjectRiskUptoDate) {
                        await Promise.all([
                            this.fetchSubjectRisks(this.email[STEPS.SUBJECT].subject),
                            this.fetchPreviewRisks(this.email[STEPS.SUBJECT].previewText),
                        ]);

                        if (this.subjectRiskStatus === CONTENT_RISK_STATUS.FAILED) {
                            throw new Error('Failed to fetch subject risk');
                        }

                        if (this.previewRiskStatus === CONTENT_RISK_STATUS.FAILED) {
                            throw new Error('Failed to fetch preview risk');
                        }

                        this.isSubjectRiskUptoDate = true;
                    }

                    if (this.subjectRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE || this.previewRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE) {
                        this.activateStep(subjectStep);
                        this.errorAdd({
                            message: this.$t('email.broadcast.editor.errors.step.subject'),
                            category: subjectStep.title,
                        });

                        return false;
                    }

                    return true;
                },

                [STEPS.AUDIENCE]: () => defaultValidator({
                    errorMessageTranslationString: 'email.broadcast.editor.errors.step.audience',
                    step: audienceStep,
                }),

                [STEPS.CONTENT]: async () => {
                    if (!this.hasContent) {
                        this.errorAdd({
                            message: this.$t('email.broadcast.editor.errors.step.content'),
                            category: contentStep.title,
                        });

                        return false;
                    }

                    this.removeAllErrorsForCategory(STEPS.CONTENT);

                    if (!this.isContentRiskUptoDate) {
                        await this.fetchContentRisks(this.email[STEPS.CONTENT].htmlBody);

                        if (this.contentRiskStatus === CONTENT_RISK_STATUS.FAILED) {
                            throw new Error('Failed to fetch content risk');
                        }

                        this.isContentRiskUptoDate = true;
                    }

                    const isValid = this.contentRiskQuality !== CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE;

                    if (!isValid) {
                        this.setStepStatus(STEPS.CONTENT, STATUSES.ERROR);
                        this.errorAdd({
                            message: this.$t('email.broadcast.editor.errors.contentRisk'),
                            category: contentStep.title,
                        });
                    } else {
                        this.setStepStatus(STEPS.CONTENT, STATUSES.COMPLETE);
                        this.removeAllErrorsForCategory(STEPS.CONTENT);
                    }

                    return isValid;
                },
            };

            const promiseList = [];

            for (const s of steps) {
                promiseList.push(validators[s]());
            }

            const results = await Promise.all(promiseList);
            const isValid = results.reduce((acc, currentVal) => acc && currentVal, true);

            for (let i = 0; i < results.length; i++) {
                const stepTitle = steps[i];
                const isStepValid = results[i];

                if (!isStepValid) {
                    this.setStepStatus(stepTitle, STATUSES.ERROR);
                }
            }

            if (!isValid && this.activeStepTitle === null) {
                const errorSteps = this.steps.filter(({ status, title }) => status === STATUSES.ERROR && title !== STEPS.CONTENT);

                if (errorSteps.length === 1) {
                    this.activateStep(errorSteps[0]);
                }
            }

            return isValid;
        },

        async handleSendTestClick() {
            try {
                this.isSendTestButtonInLoadingState = true;

                const isValid = await this.validateEmail([STEPS.SUBJECT, STEPS.CONTENT]);

                if (isValid) {
                    this.isModalOpen = true;
                }
            } catch (e) {
                this.$error({
                    message: this.$t('funnel.unlayerEmail.error.validatingEmail'),
                    bottom: true,
                });
            } finally {
                this.isSendTestButtonInLoadingState = false;
            }
        },

        async handleReady(value) {
            this.removeAllErrorsForCategory(ERROR_CATEGORIES.READY_TOGGLE);

            if (value) {
                this.isReadyStateLoading = true;

                try {
                    const isValid = await this.validateEmail([
                        STEPS.SENDER,
                        STEPS.SUBJECT,
                        STEPS.AUDIENCE,
                        STEPS.CONTENT,
                    ]);

                    if (!isValid) {
                        this.errorAdd({
                            message: this.$t('funnel.unlayerEmail.error.readyToggle'),
                            category: ERROR_CATEGORIES.READY_TOGGLE,
                        });

                        return;
                    }
                } catch (e) {
                    this.$error({
                        message: this.$t('funnel.unlayerEmail.error.validatingEmail'),
                        bottom: true,
                    });

                    return;
                } finally {
                    this.isReadyStateLoading = false;
                }
            }

            this.isReady = value;
            this.emitReadyStatus();
        },

        emitReadyStatus() {
            if (this.isKonva) {
                const eventName = this.isReady ? 'campaignUnlayerEmailReady' : 'campaignUnlayerEmailNotReady';

                this.$emit(eventName);
            } else {
                // remove when polymer is deprecated
                const eventName = this.isReady ? 'campaignUnlayerEmailReady' : 'campaignUnlayerEmailNotReady';

                window.parent.dispatchEvent(new Event(eventName));
            }
        },

        handleClose() {
            this.emitReadyStatus();

            if (this.isKonva) {
                this.$emit('closeCampaignUnlayerEmailBuilderModal');
            } else {
                window.parent.dispatchEvent(new Event('closeCampaignUnlayerEmailBuilderModal'));
            }
        },

        isStepError(step) {
            return this.showErrors
                && (step.status === STATUSES.ERROR
                || step.status === STATUSES.ERROR_SENDING_NO_RECIPIENTS
                || step.status === STATUSES.ERROR_HAS_NOT_FINISHED_EDITING);
        },

        iconClass(step) {
            if (this.isStepError(step)) {
                return STATUS_ICONS.error;
            }

            if (this.isStepActive(step)) {
                return STATUS_ICONS.active;
            }

            return STATUS_ICONS[step.status] ?? '';
        },

        stepIcon(step) {
            if (this.isStepError(step)) {
                return 'alert-triangle-fill';
            }

            return 'check';
        },

        isStepNew({ status }) {
            return status === STATUSES.NEW;
        },

        isStepActive({ title }) {
            return title === this.activeStepTitle;
        },

        activateStep({ title }) {
            if (this.steps.map((s) => s.title).includes(title)) {
                this.activeStepTitle = title;
                this.isUnlayerWizardModalOpen = (title === STEPS.CONTENT);
            }
        },

        cancelEditStep() {
            this.activeStepTitle = null;
        },

        getActionText({ title, status }, text) {
            if (status !== STATUSES.NEW) {
                return this.$t('global.edit');
            }

            if (title === STEPS.CONTENT) {
                return this.$t('email.broadcast.steps.content.actionText');
            }

            if (title === STEPS.SUBJECT) {
                return this.$t('email.broadcast.steps.subject.actionText');
            }

            return text;
        },

        setStepStatus(title, status) {
            const step = this.steps.find(({ title: t }) => title === t);

            if (step) step.status = status;
        },

        errorAdd({ message, category }) {
            const errorExists = this.errors.some(({ message: m }) => message === m);

            if (!errorExists) {
                this.errors.push({
                    message,
                    category,
                });
            }
        },

        handleStepError(step, errorCode) {
            this.showErrors = true;

            this.setStepStatus(step.title, STATUSES.ERROR);

            const errorMessage = (() => {
                switch (errorCode) {
                case STEP_ERRORS.ERROR_FETCHING_CUSTOM_DOMAINS:
                    return this.$t('email.broadcast.editor.errors.fetchingCustomDomains');
                case STEP_ERRORS.ERROR_FETCHING_USERS:
                    return this.$t('email.broadcast.editor.errors.fetchingUsers');
                case STEP_ERRORS.SENDER_NOT_FOUND:
                    return this.$t('email.campaignBuilder.unlayer.error.senderNotFound');
                case STEP_ERRORS.SENDER_DKIM_ERROR:
                    return this.$t('email.campaignBuilder.unlayer.error.senderDkimError');
                case STEP_ERRORS.SUBJECT_OR_PREVIEW_LIMIT_EXCEEDED:
                    return this.$t('email.broadcast.editor.errors.step.subject');
                default:
                    return errorCode;
                }
            })();

            this.errorAdd({
                message: errorMessage,
                category: step.title,
            });

            if (this.errors.length === 1) {
                this.activateStep(step);
            }
        },

        removeAllErrorsForCategory(cat) {
            if (this.errors.filter(({ category }) => category === cat).length === 0) return;

            this.errors = this.errors.filter(({ category }) => cat !== category);
        },

        async processDesign(save) {
            const content = this.email[STEPS.CONTENT];
            const { [STEPS.SUBJECT]: { subject, previewText } } = this.email;
            const { campaignLinks, funnelMergeFields } = this;
            const designString = JSON.stringify(content.design);

            const hasCampaignMergeFieldsWithId = (
                containsAnyCampaignMergeFieldWithId(designString)
                || containsAnyCampaignMergeFieldWithId(content.htmlBody ?? '')
                || containsAnyCampaignMergeFieldWithId(subject ?? '')
                || containsAnyCampaignMergeFieldWithId(previewText ?? '')
            );

            try {
                replaceCampaignLinkIdsWithKeys(content.design, campaignLinks);
            } catch (e) {
                // ignore
            }

            if (hasCampaignMergeFieldsWithId) {
                try {
                    const replacedDesignString = replaceCampaignMergeFieldIdsWithKeys(designString, funnelMergeFields);
                    const replacedDesign = JSON.parse(replacedDesignString);

                    const replacedSubject = replaceCampaignMergeFieldIdsWithKeys(subject, funnelMergeFields);
                    const replacedPreviewText = replaceCampaignMergeFieldIdsWithKeys(previewText, funnelMergeFields);

                    this.email[STEPS.CONTENT].design = replacedDesign;

                    this.email[STEPS.SUBJECT].subject = replacedSubject;
                    this.email[STEPS.SUBJECT].previewText = replacedPreviewText;
                } catch (e) {
                    // ignore
                }
            }

            if (save) {
                await this.saveEmail();
            }
        },

        async loadPreview({ resolve, reject }) {
            try {
                const result = await getFunnelPreviewHtml({ templateId: this.email.id, funnelId: this.funnelId })

                resolve(result);
            } catch (e) {
                reject(e);
            }
        },

        async fetchMergeFields() {
            try {
                this.mergeFields = await getMergeFields(this.funnelId);
            } catch (e) {
                this.mergeFields = [];
            }
        },

        async loadEmail() {
            const promiseArr = [];
            let funnelEmailPromise;

            if (this.marketingEmailId) {
                funnelEmailPromise = getFunnelEmail({
                    marketingEmailId: this.marketingEmailId,
                    funnelId: this.funnelId,
                });

                promiseArr.push(funnelEmailPromise);
            }

            const campaignLinksPromise = getCampaignLinks(this.funnelId);
            const funnelMergeFieldsPromise = getFunnelMergeFields(this.funnelId);

            promiseArr.push(campaignLinksPromise, funnelMergeFieldsPromise, this.fetchMergeFields());

            await Promise.all(promiseArr);

            const { links: campaignLinks } = await campaignLinksPromise;

            this.campaignLinks = campaignLinks;
            this.funnelMergeFields = await funnelMergeFieldsPromise;

            if (!this.marketingEmailId) return;

            const template = await funnelEmailPromise;

            this.trackedLinks = template.trackedLinks || [];

            this.email[STEPS.SENDER].from = template.fromAddress || DEFAULT_FROM_VALUE;
            this.email[STEPS.SENDER].replyToAddress = template.replyToAddress;

            this.email[STEPS.SUBJECT].subject = template.subject;
            this.email[STEPS.SUBJECT].previewText = template.previewText;

            this.email[STEPS.AUDIENCE].toAddress = template.toAddress || DEFAULT_RECIPIENT_VALUES_LIQUID_SYNTAX.EMAIL;

            this.email.id = template.id;
            this.email.templateId = template.id;
            this.email.templateTitle = template.title;

            if (template.design) {
                this.email[STEPS.CONTENT].design = JSON.parse(template.design);
            }
            this.email[STEPS.CONTENT].htmlBody = template.htmlBody || '<html></html>';

            if (validateUnlayerEmail(this.email, STEPS.SUBJECT)) {
                this.setStepStatus(STEPS.SUBJECT, STATUSES.COMPLETE);
            }

            if (validateUnlayerEmail(this.email, STEPS.CONTENT)) {
                this.setStepStatus(STEPS.CONTENT, STATUSES.COMPLETE);
            }

            try {
                await this.processDesign(true);
            } catch (e) {
                // ignore
            }
            this.wizardStageIndex = this.hasContent ? STAGE_INDEXES.CREATE : STAGE_INDEXES.TEMPLATE;
        },

        async saveEmail(overrides = {}) {
            const {
                marketingEmailId,
                email,
                funnelId,
                trackedLinks,
            } = this;

            const emailPayload = {
                id: marketingEmailId,
                trackedLinks,

                fromAddress: email[STEPS.SENDER].from,
                replyToAddress: email[STEPS.SENDER].replyToAddress,

                subject: email[STEPS.SUBJECT].subject,
                previewText: email[STEPS.SUBJECT].previewText,

                toAddress: email[STEPS.AUDIENCE].toAddress,

                design: overrides.design || email[STEPS.CONTENT].design,
                htmlBody: overrides.htmlBody || email[STEPS.CONTENT].htmlBody,
            };

            this.isDoneButtonInLoadingState = true;

            try {
                const { data } = await saveFunnelEmail(emailPayload, funnelId);
                const { externalTemplateId, trackedLinks: tl } = data;

                this.externalTemplateId = externalTemplateId;
                this.trackedLinks = tl;

                return data;
            } catch (e) {
                if (e?.response?.status === 403
                        && e?.response?.data?.reasonCode === EXPIRED_JWT_SUBJECT_REASON_CODE) {
                    this.$error({
                        message: this.$t('funnel.unlayerEmail.error.savingEmail.sessionExpired'),
                        actionText: this.$t('funnel.unlayerEmail.error.savingEmail.sessionExpired.action'),
                        action: () => {
                            const newWindow = window.open(SESSION_EXPIRED_LOGIN_URL, '_blank', 'noopener,noreferrer');

                            if (newWindow) {
                                newWindow.opener = null;
                            }
                        },
                        bottom: true,
                    });
                } else {
                    this.$error({
                        message: this.$t('funnel.unlayerEmail.error.savingEmail'),
                        bottom: true,
                    });
                }

                throw e;
            } finally {
                this.isDoneButtonInLoadingState = false;
            }
        },

        async saveStep(step, data) {
            this.activeStepTitle = null;

            switch (step.title) {
            case STEPS.SENDER:
                this.email[STEPS.SENDER].from = data.from;
                this.email[STEPS.SENDER].replyToAddress = data.replyToAddress;
                break;
            case STEPS.SUBJECT:
                this.email[STEPS.SUBJECT].subject = data.subject;
                this.email[STEPS.SUBJECT].previewText = data.previewText;

                // subject risk is triggered before save
                this.isSubjectRiskUptoDate = true;
                break;
            case STEPS.AUDIENCE:
                this.email[STEPS.AUDIENCE].toAddress = data.toAddress;
                break;
            default:
                return;
            }

            this.setStepStatus(step.title, STATUSES.COMPLETE);
            this.removeAllErrorsForCategory(step.title);
            this.saveEmail();
        },

        handleUnlayerWizardModalClose() {
            this.activeStepTitle = null;
            this.isUnlayerWizardModalOpen = false;

            if (this.hasContent) {
                const isValid = this.contentRiskQuality !== CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE;

                this.setStepStatus(STEPS.CONTENT, isValid ? STATUSES.COMPLETE : STATUSES.ERROR);

                if (isValid) {
                    this.removeAllErrorsForCategory(STEPS.CONTENT);
                } else {
                    this.showErrors = true;
                }
            }
        },

        handleTemplateUpdate(data) {
            const {
                design,
                htmlBody,
                trackedLinks,
            } = data;

            this.email[STEPS.CONTENT].design = design;
            this.email[STEPS.CONTENT].htmlBody = htmlBody;

            if (Array.isArray(trackedLinks)) {
                this.trackedLinks = trackedLinks;
            }

            this.isContentRiskUptoDate = false;
        },

        handleTemplateChange(data) {
            this.handleTemplateUpdate(data);

            if (validateUnlayerEmail(this.email, STEPS.CONTENT)) {
                // template is saved as soon as unlayer editor
                // loads anyway so we could skip saving it in this case
                this.processDesign(false);

                this.setStepStatus(STEPS.CONTENT, STATUSES.COMPLETE);
                this.wizardStageIndex = STAGE_INDEXES.CREATE;
            }
        },

        async handleUnsavedChanges({ design, htmlBody }) {
            const data = await this.saveEmail({ design, htmlBody });

            this.email[STEPS.CONTENT].design = JSON.parse(data.design);
            this.email[STEPS.CONTENT].htmlBody = data.htmlBody;
        },

        async handleDone() {
            this.isDoneButtonInLoadingState = true;

            try {
                const isValid = await this.validateEmail([
                    STEPS.SENDER,
                    STEPS.SUBJECT,
                    STEPS.AUDIENCE,
                    STEPS.CONTENT,
                ]);

                if (!isValid) {
                    return;
                }
            } catch (e) {
                this.$error({
                    message: this.$t('funnel.unlayerEmail.error.validatingEmail'),
                    bottom: true,
                });

                return;
            } finally {
                this.isDoneButtonInLoadingState = false;
            }

            try {
                await this.saveEmail();
            } catch (e) {
                return;
            }

            this.handleClose();
        },
    },
};
</script>

<style scoped lang="scss">
.init-spinner-container {
    width: 100%;
    height: px-to-rem(200);
    display: flex;
    justify-content: center;
    align-items: center;
}

.sequence-toggle {
    font-weight: normal;
    font-size: $font-size-sm;

    padding-top: 0; /* a style from sink_css.jsp is changing this */
}

.ready-status-spinner {
    margin-right: $spacing-200;
}

.error-container {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    margin: 0 auto;

    @media($small) {
        margin: 0 $spacing-200;
    }

    > *:not(:first-child) {
        margin-top: $spacing-200;
    }
}

.editor-content {
    max-width: px-to-rem(1024);
    margin: $spacing-200 auto;
    width: 100%;
}

.send-test-email-modal {
    :deep(.modal-content-wrapper),
    :deep(.modal-header),
    :deep(.modal-body) {
        border-radius: px-to-rem(10);
    }
}
</style>
